import { Primary500 } from '@mehilainen/mds-customer/colors'
import { Button, styled } from '@mui/material'
import React, { ReactNode, useCallback, useState } from 'react'
import { useTranslation, Trans } from 'react-i18next'

import { GetOptionsResult, SupportedLanguage } from '../../../../__generated__/api'
import { DefaultButton } from '../../../../common/components/DefaultButton/DefaultButton'
import Modal from '../../../../common/components/Modal/Modal'
import { Text } from '../../../../common/components/Typography/Typography'
import api from '../../../../common/services/api'
import { scale } from '../../../../common/utils/scale'
import { ReserveCheckbox } from '../FormComponents'

import InsuranceVerificationFeedback from './InsuranceVerificationFeedback'
import {
  ButtonsContainer,
  CheckboxContainer,
  ContentContainer,
  Heading,
  LongText,
  Paragraph,
} from './Shared'

interface Props {
  insurancesApiInsuranceCompanyId: string
  subjectOmUid: number
  Logo: React.FC<React.PropsWithChildren<unknown>>
  translationKey: string
  incidentType: string | undefined
  onSuccess(): void
  onBack(): void
  onContinueAsSelfPaying(): void
  insuranceOptions: GetOptionsResult
  redirectToPrivateInsurance?: () => void // Optional callback for handling fallback
}

interface FeedbackState {
  viewState: 'default' | 'error' | 'no-customership' | 'no-policy-for-claim-type'
  heading?: string
  text?: string | ReactNode
  onContinueCallback?: () => void
  onBackCallback?: () => void
  onRedirectToFallbackInsurance?: () => void
}

const CustomershipCheckCompanyFlow: React.FC<React.PropsWithChildren<Props>> = ({
  insurancesApiInsuranceCompanyId,
  subjectOmUid,
  translationKey,
  Logo,
  incidentType,
  onSuccess,
  onBack,
  onContinueAsSelfPaying,
  insuranceOptions,
  redirectToPrivateInsurance,
}) => {
  const { t, i18n } = useTranslation()
  const [loading, setLoading] = useState<boolean>(false)
  const [consent, setConsent] = useState<boolean>(false)
  const [feedbackState, setFeedbackState] = useState<FeedbackState>({ viewState: 'default' })
  const [modalOpen, setModalOpen] = useState<boolean>(false)

  const currentLanguage = i18n.language as SupportedLanguage

  const onCheck = useCallback(() => {
    setLoading(true)

    const handleError = () => {
      setFeedbackState({
        viewState: 'error',
        heading: t(`component.pohjolaForm.customershipCheck.error.heading`),
        text: (
          <Trans
            i18nKey="component.pohjolaForm.customershipCheck.error.text"
            t={t}
            components={{
              linkTag: <a href={t(`component.pohjolaForm.customershipCheck.error.link`)} />,
            }}
          />
        ),
        onContinueCallback: onContinueAsSelfPaying,
        onBackCallback: onBack,
      })
    }

    api.v1
      .insuranceCustomership({
        subjectOmUid,
        insuranceCompanyId: insurancesApiInsuranceCompanyId,
        lang: i18n.language as SupportedLanguage,
        incidentType,
      })
      .then((res) => {
        setLoading(false)

        if (res.error) {
          handleError()
          return
        }

        if (res.data.hasCustomership && res.data.hasPolicyForClaimType) {
          onSuccess()
          return
        }

        // Get i18n messages from API response
        const heading = res.data.i18n?.failedTitle?.[currentLanguage]
        const text = res.data.i18n?.failedText?.[currentLanguage]

        if (res.data.hasCustomership === false) {
          setFeedbackState({
            viewState: 'no-customership',
            heading,
            text,
            onContinueCallback: onContinueAsSelfPaying,
            onBackCallback: onBack,
            onRedirectToFallbackInsurance: redirectToPrivateInsurance,
          })
        } else if (res.data.hasPolicyForClaimType === false) {
          setFeedbackState({
            viewState: 'no-policy-for-claim-type',
            heading,
            text,
            onContinueCallback: onContinueAsSelfPaying,
            onBackCallback: onBack,
            onRedirectToFallbackInsurance: redirectToPrivateInsurance,
          })
        }
      })
      .catch(() => {
        setLoading(false)
        handleError()
      })
  }, [
    subjectOmUid,
    insurancesApiInsuranceCompanyId,
    i18n.language,
    incidentType,
    onSuccess,
    onBack,
    onContinueAsSelfPaying,
    redirectToPrivateInsurance,
    t,
    currentLanguage,
  ])

  const StyledText = styled(Text)`
    margin-top: ${scale(3)};
    white-space: pre-wrap;
  `

  const hasModalTitle =
    insuranceOptions.i18n?.usingServiceCheckCustomerShipInfoLinkText?.[currentLanguage]
  const hasModalText =
    insuranceOptions.i18n?.usingServiceCheckCustomerShipInfoText?.[currentLanguage]

  const hasModalContents = hasModalTitle && hasModalText

  // If we're in a feedback state, render the InsuranceVerificationFeedback component
  if (feedbackState.viewState !== 'default') {
    return (
      <InsuranceVerificationFeedback
        translationKey={translationKey}
        heading={feedbackState.heading || ''}
        text={feedbackState.text}
        onContinueAsSelfPaying={onContinueAsSelfPaying}
        onBack={feedbackState.onBackCallback || onBack}
        fallbackInsurance={feedbackState.onRedirectToFallbackInsurance}
      />
    )
  }

  // Extract titles and text from API response
  const checkCustomershipTitle =
    insuranceOptions.i18n?.usingServiceCheckCustomerShipTitle?.[currentLanguage] ||
    t(`component.${translationKey}.customershipCheck.heading`)
  const checkCustomershipText =
    insuranceOptions.i18n?.usingServiceCheckCustomerShipText?.[currentLanguage] ||
    t(`component.${translationKey}.customershipCheck.paragraphs`)
  const checkCustomershipCheckboxText =
    insuranceOptions.i18n?.usingServiceCheckCustomerShipCheckBoxText?.[currentLanguage] ||
    t(`component.${translationKey}.customershipCheck.consentCheckboxLabel`)

  return (
    <ContentContainer>
      <Logo />
      <Heading>{checkCustomershipTitle}</Heading>
      <LongText>{checkCustomershipText}</LongText>
      {hasModalContents && (
        <Button onClick={() => setModalOpen(true)} variant="text">
          <Paragraph $color={Primary500}>{hasModalTitle}</Paragraph>
        </Button>
      )}
      <CheckboxContainer>
        <ReserveCheckbox
          name="checkCustomership"
          checked={consent}
          onChange={() => setConsent(!consent)}
          label={checkCustomershipCheckboxText}
        />
      </CheckboxContainer>
      <ButtonsContainer>
        <DefaultButton onClick={onCheck} disabled={loading || !consent}>
          {t(`component.${translationKey}.customershipCheck.checkButtonText`)}
        </DefaultButton>
        <DefaultButton variant="outlined" onClick={onContinueAsSelfPaying}>
          {t(`component.${translationKey}.customershipCheck.continueAsSelfPaying`)}
        </DefaultButton>
        <DefaultButton variant="text" onClick={onBack}>
          {t('common.goBack')}
        </DefaultButton>
      </ButtonsContainer>
      {hasModalContents && (
        <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
          <StyledText $size={300} $height="Large">
            {hasModalText}
          </StyledText>
        </Modal>
      )}
    </ContentContainer>
  )
}

export default CustomershipCheckCompanyFlow
